import { intToFormattedString } from '../lib/util';

/*
 * Carrier transit commitments (TTI-21097).
 *
 * Collaterate now captures what the carrier commits to when it rates a shipment, and stores it on the
 * quote as `deliverBy` (an Instant) and `transitTime` (a Duration, preferring the carrier's own count
 * and falling back to the ship-by to deliver-by gap). `ShippingServiceQuoteDTO` - what POST
 * /shippingQuotes returns, and what this model maps - does not expose either yet, so both properties
 * below read null until it does. They are wired now so consumers can build against them.
 *
 * The field names are the strongest evidence available rather than a confirmed contract:
 * `daysInTransit` is already the storefront's own wire name for this on ShippingEstimateDTO (the
 * cart-side estimate, mapped by ShippingServiceQuote), and `deliverBy` is the quote's own field name
 * server side. Both are read in one place so correcting them is a one-line change if the exposing
 * DTO chooses otherwise.
 */

const DAYS_IN_TRANSIT_FIELD = 'daysInTransit';
const DELIVER_BY_FIELD = 'deliverBy';

const toWholeDays = value => (typeof value === 'number' && Number.isFinite(value) ? Math.round(value) : null);

/*
 * A carrier that cannot commit is still quoting a valid rate, so anything unparseable degrades to
 * null rather than an Invalid Date that would reach a shopper as "NaN".
 */
const toDate = value => {
    if (!value) {
        return null;
    }

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : Object.freeze(date);
};

/**
 * A selectable shipping choice at checkout.
 *
 * Most rates are real carrier or site services quoted by `POST /shippingQuotes`. Two are synthesized
 * client side to match the storefront checkout, and are told apart by
 * {@link CheckoutRate#isUSPSService isUSPSService} and {@link CheckoutRate#isCustomService
 * isCustomService}: a flat-rate USPS option for sample requests, and a "describe it yourself" option
 * for sites that allow one. Both carry a string {@link CheckoutRate#serviceId serviceId} rather than a
 * numeric one, and both save differently - see checkoutRates.js.
 *
 * @property {string} key - (Read only) A stable key unique across the rate list, safe to use as a form value.
 * @property {number|string} serviceId - (Read only) The shipping service id. A string sentinel for the two synthesized options.
 * @property {string} name - (Read only) The display name of the shipping service.
 * @property {boolean} isVendorService - (Read only) Whether this is a carrier service rather than a site-specific one. Required when saving the selection.
 * @property {boolean} isUSPSService - (Read only) Whether this is the flat-rate USPS option.
 * @property {boolean} isCustomService - (Read only) Whether this is the shopper-described option, in which case a description must be supplied when saving.
 * @property {?number} price - (Read only) The price. Null when the service carries no price, as with a shopper-described service.
 * @property {string} displayPrice - (Read only) this.price as string, formatted with commas and two decimal places. Empty when there is no price.
 * @property {?number} daysInTransit - (Read only) Days the shipment takes to arrive after leaving the supplier, as committed by the carrier. Null whenever the carrier makes no commitment, which includes both synthesized options. **The API does not send this yet** - see the note at the top of this file.
 * @property {?Date} estimatedDeliveryDate - (Read only) The date the carrier commits to delivering by. Null whenever the carrier makes no commitment. **The API does not send this yet** - see the note at the top of this file.
 * @property {boolean} hasDeliveryEstimate - (Read only) Whether this rate carries any carrier commitment at all, so a consumer can decide whether to render a delivery column without null-checking two fields.
 */

class CheckoutRate {
    constructor (data = {}) {
        this.serviceId = data.serviceId;
        this.name = data.serviceName;
        this.isVendorService = Boolean(data.isVendorService);
        this.isUSPSService = Boolean(data.isUSPSService);
        this.isCustomService = Boolean(data.isCustomService);
        this.price = typeof data.price === 'number' ? data.price : null;
        this.displayPrice = this.price === null ? '' : intToFormattedString(this.price.toFixed(2));

        this.daysInTransit = toWholeDays(data.daysInTransit);
        this.estimatedDeliveryDate = toDate(data.deliverBy);
        this.hasDeliveryEstimate = this.daysInTransit !== null || this.estimatedDeliveryDate !== null;

        /*
         * The same numeric service id can appear as both a vendor and a site service, so neither alone
         * is unique. The synthesized options have no such collision and key on their sentinel id, which
         * also matches how the storefront checkout keys them.
         */
        this.key = this.isUSPSService || this.isCustomService
            ? `${this.serviceId}`
            : `${this.serviceId}_${this.isVendorService}`;

        Object.freeze(this);
    }
}

export default CheckoutRate;
